import { Typography } from '@mui/material'
import type { Step } from 'react-joyride'
import { Link } from 'react-router-dom'
import type { Breakpoint } from '~hooks/use-breakpoint'
import { lastStep } from './common-steps'

export interface FinancialSubpage {
  title: string
  link: string
}

export function constructFinancialsTour({
  breakpoint,
  subpages,
  vesselLink,
}: {
  breakpoint: Breakpoint
  subpages: FinancialSubpage[]
  vesselLink?: string
}): Step[] {
  const isSmallSizeTour = ['xs', 'sm', 'md'].includes(breakpoint)
  const vesselId = vesselLink
    ? new URLSearchParams(vesselLink.split('?')[1]).get('vesselId')
    : null
  const vesselPages = vesselLink
    ? [
        { title: 'Vessel Adjustments', link: vesselLink },
        {
          title: 'Vessel Pool Points',
          link: vesselLink.replace('/adjustments?', '/pool-points?'),
        },
      ]
    : []
  const links = Object.fromEntries(
    subpages.map(({ title, link }) => [title, link]),
  )
  const steps: Record<string, Step> = {
    'Technical Management': {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Technical Management
          </Typography>
          <Typography variant="paragraph2" component="p">
            Here you can see the vessels under your technical management. Click
            on a vessel to view its details.
          </Typography>
        </>
      ),
      target: `.tour-step-nav-pane a[href="${links['Technical Management']}"]`,
      placement: isSmallSizeTour ? 'bottom' : 'right',
    },
    Earnings: {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Earnings
          </Typography>
          <Typography variant="paragraph2" component="p">
            Earnings provides an overview of how the vessels in your pool have
            performed over time. This is similar to page 2 of the monthly
            report.
          </Typography>
        </>
      ),
      target: `.tour-step-nav-pane a[href="${links.Earnings}"]`,
      placement: isSmallSizeTour ? 'bottom' : 'right',
    },
    'Earnings columns': {
      target: '.tour-step-earnings-column-header',
      placement: 'top',
      targetWaitTimeout: 3000,
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Sort and explore columns
          </Typography>
          <Typography variant="paragraph2" component="p">
            Click a column heading to sort the table.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            Hover over information icons beside headings to see helpful details.
          </Typography>
        </>
      ),
    },
    'Earnings vessel': {
      target: vesselId
        ? () =>
            document.querySelector<HTMLElement>(
              `.tour-step-earnings-vessel-link[href$="?vesselId=${encodeURIComponent(vesselId)}"]`,
            ) ??
            document.querySelector<HTMLElement>(
              '.tour-step-earnings-vessel-link',
            )
        : '.tour-step-earnings-vessel-link',
      placement: 'right',
      targetWaitTimeout: 1000,
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Open a vessel
          </Typography>
          <Typography variant="paragraph2" component="p">
            Click on a vessel to see the vessel’s adjustment schedule. The same
            feature is available in the Hire Payments section.
          </Typography>
          {vesselPages.length > 0 && (
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Next, we’ll select a vessel and explain its Adjustments and Pool
              Points pages.
            </Typography>
          )}
        </>
      ),
    },
    'Vessel Adjustments': {
      target: 'body',
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Adjustments
          </Typography>
          <Typography variant="paragraph2" component="p">
            Review adjustments applied to the selected vessel’s earnings for the
            chosen period. Use the year and posting date filters to narrow the
            adjustments shown.
          </Typography>
        </>
      ),
    },
    'Vessel Pool Points': {
      target: 'body',
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Pool Points
          </Typography>
          <Typography variant="paragraph2" component="p">
            Review the vessel specifications and calculations used to determine
            its share of pool earnings.
          </Typography>
        </>
      ),
    },
    'Average Distribution': {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Average Distribution
          </Typography>
          <Typography variant="paragraph2" component="p">
            See how pool earnings are distributed across vessels over time. This
            is similar to page 1 of the monthly report.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can find charter hire statements on the Documents page in the
            main menu, under the Charter Hire Statements category.
          </Typography>
        </>
      ),
      target: `.tour-step-nav-pane a[href="${links['Average Distribution']}"]`,
      placement: isSmallSizeTour ? 'bottom' : 'right',
    },
    'Hire Payments': {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Hire Payments
          </Typography>
          <Typography variant="paragraph2" component="p">
            Review the calculation of charter hire payments. This is similar to
            page 3 of the monthly report.
          </Typography>
        </>
      ),
      target: `.tour-step-nav-pane a[href="${links['Hire Payments']}"]`,
      placement: isSmallSizeTour ? 'bottom' : 'right',
    },
    Forecast: {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Forecast
          </Typography>
          <Typography variant="paragraph2" component="p">
            Forecast (formerly known as Trends) shows a fixed and unfixed
            outlook for your selected pool. The underlying data remains the same
            and is simply presented under a different name.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            When a month is less than 30% fixed, only the weighted TCE for fixed
            voyage results is shown. Once a month reaches 30% fixed, projections
            for the unfixed days are included and weighted with the fixed
            results.
          </Typography>
        </>
      ),
      target: `.tour-step-nav-pane a[href="${links.Forecast}"]`,
      placement: isSmallSizeTour ? 'bottom' : 'right',
    },
    'Pool Points': {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Pool Points
          </Typography>
          <Typography variant="paragraph2" component="p">
            Review the vessel specifications and calculations used to determine
            its share of pool earnings. These Pool Points are the ones that have
            been formally approved at pool meetings.
          </Typography>
        </>
      ),
      target: `.tour-step-nav-pane a[href="${links['Pool Points']}"]`,
      placement: isSmallSizeTour ? 'bottom' : 'right',
    },
  }

  const pages = subpages
    .filter(({ title }) => !['Reports', 'Compare Vessels'].includes(title))
    .flatMap((page) =>
      page.title === 'Earnings'
        ? [
            page,
            { title: 'Earnings columns', link: page.link },
            { title: 'Earnings vessel', link: page.link },
            ...vesselPages,
          ]
        : [page],
    )
  const inPageSteps = ['Earnings columns', 'Earnings vessel']

  return [
    {
      content: (
        <>
          <Typography variant="label" sx={{ mb: 3 }} component="p">
            Welcome to Financials
          </Typography>
          <Typography variant="paragraph2" component="p">
            Financials brings together your pool’s earnings, distributions,
            forecasts and reports, as well as the financial details for each
            vessel.
          </Typography>
          <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
            You can skip the tour at any point, and you can always start it
            again from the <Link to="/help">Help page</Link>.
          </Typography>
        </>
      ),
      target: '.tour-step-nav-financials',
      placement: isSmallSizeTour ? 'bottom' : 'right',
      skipBeacon: true,
      targetWaitTimeout: 15000,
      data: {
        navigationLink: pages[0]?.link,
        next: pages[0]?.link,
        deferredTarget: true,
      },
    },
    ...pages.map<Step>(({ title, link }, index) => {
      const isInPageStep = inPageSteps.includes(title)
      const menuLink = link.split('?')[0]
      const step = steps[title] ?? {
        content: (
          <>
            <Typography variant="label" sx={{ mb: 3 }} component="p">
              {title}
            </Typography>
            <Typography variant="paragraph2" component="p">
              Open {title} to explore the financial information available to
              you.
            </Typography>
          </>
        ),
      }
      return {
        ...step,
        ...(isInPageStep
          ? {}
          : {
              target: `.tour-step-nav-pane a[href="${menuLink}"]`,
              placement: isSmallSizeTour ? 'bottom' : 'right',
            }),
        skipBeacon: true,
        targetWaitTimeout: step.targetWaitTimeout ?? 15000,
        data: {
          navigationLink: isInPageStep ? undefined : menuLink,
          previous:
            pages[index - 1]?.link === link
              ? undefined
              : pages[index - 1]?.link,
          next:
            pages[index + 1]?.link === link
              ? undefined
              : pages[index + 1]?.link,
          deferredTarget: true,
        },
      }
    }),
    {
      ...lastStep,
      data: {
        previous: pages[pages.length - 1]?.link,
        deferredTarget: true,
      },
    },
  ]
}
